/**
 * BankLoan Summary Card sums — same date-window math as RepaymentScheduleDrawer,
 * usable without opening BankLoanDetail (MULTI save background recalc).
 */
import {
    addDays,
    addYears,
    formatDDMMYYYY,
    formatYYYYMMDD,
    getLastBalanceByInstalmentNos,
    instalmentNosInDateRange,
    parseAnyDate,
    parseDDMMYYYY,
    sumByRange,
    sumScheduleFieldByInstalmentNos,
} from '@/utils/dataUtils';
import {
    calcBankLoanSchedule,
    type LoanScheduleItemVO,
    type LoanScheduleRequest,
} from '@/services/auditService/auditLoanCalculatorService';
import type {BankLoanSums} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/RepaymentScheduleDrawer';
import type {BankLoanRow, BankLoanSum} from '@/services/auditService/interface/wp-row';
import {normalizeLegacyLoanSums} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/loanSummarySums';

export type BankLoanSumPeriodWindow = {
    periodFrom?: string | null;
    periodTo?: string | null;
    lastYearPeriodFrom?: string | null;
    lastYearPeriodTo?: string | null;
};

/** Build calcBankLoanSchedule request from a loan row (same as BankLoanDetail). */
export function buildBankLoanScheduleRequest(
    loan: Pick<
        BankLoanRow,
        | 'loanAmt'
        | 'ratePct'
        | 'terms'
        | 'firstRepay'
        | 'interestFreeFrom'
        | 'interestFreeTo'
        | 'principalFreeFrom'
        | 'principalFreeTo'
    > | null | undefined,
): LoanScheduleRequest | null {
    if (!loan) return null;
    const amt = Number(loan.loanAmt) || 0;
    const rate = Number(loan.ratePct) || 0;
    const n = Number(loan.terms) || 0;
    const first = parseDDMMYYYY(loan.firstRepay) || parseAnyDate(loan.firstRepay);
    if (!amt || !rate || !n || !first) return null;
    return {
        loanAmount: amt,
        tenorYears: n / 12,
        annualInterestRatePercent: rate,
        firstRepaymentDate: formatYYYYMMDD(first),
        interestFreeFrom: loan.interestFreeFrom || null,
        interestFreeTo: loan.interestFreeTo || null,
        principalFreeFrom: loan.principalFreeFrom || null,
        principalFreeTo: loan.principalFreeTo || null,
    };
}

export function bankLoanScheduleCacheKey(req: LoanScheduleRequest): string {
    return [
        req.loanAmount,
        req.tenorYears,
        req.annualInterestRatePercent ?? '',
        req.firstRepaymentDate,
        req.totalInstalment ?? '',
        req.interestFreeFrom ?? '',
        req.interestFreeTo ?? '',
        req.principalFreeFrom ?? '',
        req.principalFreeTo ?? '',
    ].join('|');
}

/**
 * Drop instalments after earlyRepay (inclusive on that date).
 * Empty / unparseable date keeps the full schedule.
 */
export function clipBankLoanScheduleAtEarlyRepay(
    schedule: LoanScheduleItemVO[] | undefined | null,
    earlyRepay?: string | null,
): LoanScheduleItemVO[] {
    const rows = schedule || [];
    const cut = parseDDMMYYYY(earlyRepay) || parseAnyDate(earlyRepay);
    if (!cut) return rows;
    const cutMs = cut.getTime();
    return rows.filter((row) => {
        const d = parseAnyDate(row?.repaymentDate);
        return !d || d.getTime() <= cutMs;
    });
}

/**
 * YOA Current / Non-current from yellow (next YOA) rows; lastYoa Interest / Balance from blue.
 * No rows in that colour → 0. Partial year → only those highlighted instalments (not a padded 12 months).
 */
export function sumsFromScheduleHighlightWindows(
    schedule: LoanScheduleItemVO[] | null | undefined,
    lastYoaInstalmentNos: number[],
    nextYoaInstalmentNos: number[],
    prevFinalOverride?: number | null,
): {currentYoa: number; nonCurrentYoa: number; interestLastYoa: number; balanceLastYoa: number} {
    const currentYoa = sumScheduleFieldByInstalmentNos(schedule, nextYoaInstalmentNos, 'principalPayment');
    const nonCurrentYoa = getLastBalanceByInstalmentNos(schedule, nextYoaInstalmentNos);
    const interestLastYoa = sumScheduleFieldByInstalmentNos(schedule, lastYoaInstalmentNos, 'interestPayment');
    const balanceLastYoa = lastYoaInstalmentNos.length === 0
        ? 0
        : (prevFinalOverride != null
            ? prevFinalOverride
            : getLastBalanceByInstalmentNos(schedule, lastYoaInstalmentNos));
    return {currentYoa, nonCurrentYoa, interestLastYoa, balanceLastYoa};
}

/**
 * Summary base fields from full schedule + YOA windows (RepaymentScheduleDrawer colours):
 * - Interest yoa / lastYoa = Interest column sum (green / blue)
 * - <1yr yoa / lastYoa = Principal sum (yellow / green)
 * - >1yr yoa = last Balance in yellow (next) window
 * - Total lastYoa = last Balance in blue (last-year) window → balanceLastYoa
 * earlyRepay clips instalments after that date before the window sums.
 */
export function computeBankLoanSumsFromSchedule(
    schedule: LoanScheduleItemVO[] | undefined | null,
    period: BankLoanSumPeriodWindow,
    earlyRepay?: string | null,
): BankLoanSums {
    const currFromDate = parseAnyDate(period.periodFrom ?? undefined);
    const currToDate = parseAnyDate(period.periodTo ?? undefined);
    const pt = parseAnyDate(period.periodTo || undefined);
    const nextPeriodFrom = pt ? addDays(pt, 1) : null;
    const nextPeriodTo = pt ? addYears(pt, 1) : null;
    const rows = clipBankLoanScheduleAtEarlyRepay(schedule, earlyRepay);
    const nyFrom = nextPeriodFrom ? formatDDMMYYYY(nextPeriodFrom) : null;
    const nyTo = nextPeriodTo ? formatDDMMYYYY(nextPeriodTo) : null;
    const inNext = instalmentNosInDateRange(rows, nyFrom, nyTo);
    const inPrev = instalmentNosInDateRange(rows, period.lastYearPeriodFrom, period.lastYearPeriodTo);
    const highlighted = sumsFromScheduleHighlightWindows(rows, inPrev, inNext);

    return {
        interestYoa: sumByRange(rows, 'interestPayment', currFromDate, currToDate),
        currentLastYoa: sumByRange(rows, 'principalPayment', currFromDate, currToDate),
        interestLastYoa: highlighted.interestLastYoa,
        currentYoa: highlighted.currentYoa,
        nonCurrentYoa: highlighted.nonCurrentYoa,
        interestNext: sumByRange(rows, 'interestPayment', nextPeriodFrom, nextPeriodTo),
        balanceLastYoa: highlighted.balanceLastYoa,
    };
}

/** Derived display-only keys — none after Balance lastYoa is persisted. */
const BANK_LOAN_SUM_CLIENT_ONLY_KEYS = [] as const;

/** Summary / schedule override keys — only wire when this Tab actually set them. */
const BANK_LOAN_SUM_OVERRIDE_KEYS = [
    'interestYoaChange',
    'interestLastYoaChange',
    'currentYoaChange',
    'currentLastYoaChange',
    'nonCurrentYoaChange',
    'balanceLastYoaChange',
    'prevFinalBalance',
    'currFinalBalance',
    'editedInstalments',
] as const;

/** Summary Card MoneyField pairs: left = calculated xxx, right = xxxChange. */
const BANK_LOAN_SUM_BASE_CHANGE_PAIRS = [
    ['interestYoa', 'interestYoaChange'],
    ['interestLastYoa', 'interestLastYoaChange'],
    ['currentYoa', 'currentYoaChange'],
    ['currentLastYoa', 'currentLastYoaChange'],
    ['nonCurrentYoa', 'nonCurrentYoaChange'],
    ['balanceLastYoa', 'balanceLastYoaChange'],
] as const;

function isPresentSumOverride(v: unknown): boolean {
    if (v == null) return false;
    if (v === '') return false;
    return true;
}

/**
 * BankLoan "Repayment during the year" (= currentLastYoa / currentLastYoaChange):
 * schedule / Detail keep positive magnitude; WP table + save wire use outflow (negative).
 */
export function toNegativeBankLoanRepayment(value: unknown): number {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return 0;
    return -Math.abs(n);
}

/** Save wire only: persist repayment fields as negative (does not mutate schedule in-memory). */
export function applyBankLoanRepaymentSignForSave(
    sums: Partial<BankLoanSum> | BankLoanSums | null | undefined,
): any {
    if (!sums || typeof sums !== 'object') return sums;
    const out: Record<string, unknown> = {...sums};
    if (out.currentLastYoa != null && out.currentLastYoa !== '') {
        out.currentLastYoa = toNegativeBankLoanRepayment(out.currentLastYoa);
    }
    if (isPresentSumOverride(out.currentLastYoaChange)) {
        out.currentLastYoaChange = toNegativeBankLoanRepayment(out.currentLastYoaChange);
    }
    return out;
}

/**
 * Detail UI: when *Change is present and equals base (save wire bled Change into xxx),
 * clear base so MoneyField left does not render the override as "calculated".
 * Schedule merge restores true base; skip once scheduleBaseReady.
 * Abs currentLastYoa* so reload of negative save-wire values still show as magnitude.
 */
export function normalizeBankLoanSumsForDetailDisplay(
    sums: Partial<BankLoanSum> | BankLoanSums | null | undefined,
): BankLoanSums {
    const out: BankLoanSums = {
        interestYoa: 0,
        currentLastYoa: 0,
        interestLastYoa: 0,
        currentYoa: 0,
        nonCurrentYoa: 0,
        interestNext: 0,
        balanceLastYoa: undefined,
        ...normalizeLegacyLoanSums(sums || {}),
    };
    for (const [baseKey, changeKey] of BANK_LOAN_SUM_BASE_CHANGE_PAIRS) {
        const change = out[changeKey];
        if (!isPresentSumOverride(change)) continue;
        const base = out[baseKey];
        if (base != null && Number(base) === Number(change)) {
            out[baseKey] = 0;
        }
    }
    // Save wire stores repayment as negative; Detail / schedule math uses magnitude.
    if (out.currentLastYoa != null && Number.isFinite(Number(out.currentLastYoa))) {
        out.currentLastYoa = Math.abs(Number(out.currentLastYoa));
    }
    if (isPresentSumOverride(out.currentLastYoaChange)) {
        out.currentLastYoaChange = Math.abs(Number(out.currentLastYoaChange));
    }
    return out;
}

/**
 * Save wire: keep base sum fields; omit unset *Change / schedule overrides;
 * pass through explicit null so backend can clear (cancel icon).
 */
export function sanitizeBankLoanSumsForSave(sums: Partial<BankLoanSum> | BankLoanSums | null | undefined): any {
    if (!sums || typeof sums !== 'object') return sums;
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(sums)) {
        if ((BANK_LOAN_SUM_CLIENT_ONLY_KEYS as readonly string[]).includes(key)) continue;
        if ((BANK_LOAN_SUM_OVERRIDE_KEYS as readonly string[]).includes(key)) {
            if (value === null) out[key] = null;
            else if (isPresentSumOverride(value)) out[key] = value;
            continue;
        }
        if ((key === 'id' || key === 'bankLoanId') && !isPresentSumOverride(value)) continue;
        out[key] = value;
    }
    return out;
}

/** Refresh base sums; keep manual *Change (split right / keyboard-only) and schedule overrides (+ row ids). */
export function mergeBankLoanSumsPreserveChanges(
    existing: Partial<BankLoanSum> | null | undefined,
    computed: BankLoanSums,
): BankLoanSum {
    // Base fields from schedule formulas; do not copy *Change from computed (Drawer never sends them).
    const merged: BankLoanSum = {
        interestYoa: computed.interestYoa,
        currentLastYoa: computed.currentLastYoa,
        interestLastYoa: computed.interestLastYoa,
        currentYoa: computed.currentYoa,
        nonCurrentYoa: computed.nonCurrentYoa,
        interestNext: computed.interestNext,
        balanceLastYoa: computed.balanceLastYoa,
    };
    if (existing?.id != null) merged.id = existing.id;
    if (existing?.bankLoanId != null) merged.bankLoanId = existing.bankLoanId;
    if (isPresentSumOverride(existing?.interestYoaChange)) {
        merged.interestYoaChange = existing!.interestYoaChange;
    }
    if (isPresentSumOverride(existing?.interestLastYoaChange)) {
        merged.interestLastYoaChange = existing!.interestLastYoaChange;
    }
    if (isPresentSumOverride(existing?.currentYoaChange)) {
        merged.currentYoaChange = existing!.currentYoaChange;
    }
    if (isPresentSumOverride(existing?.currentLastYoaChange)) {
        merged.currentLastYoaChange = existing!.currentLastYoaChange;
    }
    if (isPresentSumOverride(existing?.nonCurrentYoaChange)) {
        merged.nonCurrentYoaChange = existing!.nonCurrentYoaChange;
    }
    if (isPresentSumOverride(existing?.balanceLastYoaChange)) {
        merged.balanceLastYoaChange = existing!.balanceLastYoaChange;
    }
    // Schedule overrides: computed key present (incl. null) wins; else keep existing.
    if (Object.prototype.hasOwnProperty.call(computed, 'prevFinalBalance')) {
        merged.prevFinalBalance = computed.prevFinalBalance;
    } else if (isPresentSumOverride(existing?.prevFinalBalance)) {
        merged.prevFinalBalance = existing!.prevFinalBalance;
    }
    if (Object.prototype.hasOwnProperty.call(computed, 'currFinalBalance')) {
        merged.currFinalBalance = computed.currFinalBalance;
    } else if (isPresentSumOverride(existing?.currFinalBalance)) {
        merged.currFinalBalance = existing!.currFinalBalance;
    }
    if (Object.prototype.hasOwnProperty.call(computed, 'editedInstalments')) {
        merged.editedInstalments = computed.editedInstalments;
    } else if (isPresentSumOverride(existing?.editedInstalments)) {
        merged.editedInstalments = existing!.editedInstalments;
    }
    return sanitizeBankLoanSumsForSave(merged) as BankLoanSum;
}

/**
 * Summary Total lastYoa resolved value: balanceLastYoaChange ?? balanceLastYoa.
 * Used for B/F (currentTotalInput) and >1yr lastYoa derivation.
 */
export function resolveBankLoanLastYoaTotal(
    sums: Partial<BankLoanSum> | BankLoanSums | null | undefined,
    fallback = 0,
): number {
    if (!sums) return fallback;
    if (isPresentSumOverride(sums.balanceLastYoaChange)) {
        return Number(sums.balanceLastYoaChange) || 0;
    }
    if (sums.balanceLastYoa != null) {
        return Number(sums.balanceLastYoa) || 0;
    }
    return fallback;
}

/**
 * BankLoan table b/f = Detail SummaryCard last-YOA Total row
 * (Change ?? balanceLastYoa ?? lastYoaColumnDisplay.total ?? currentTotalInput).
 * Same resolve order as BankLoanDetail lastYoaTotalDisplay.
 */
export function resolveBankLoanBfFromSummaryLastYoaTotal(
    loan: {
        currentTotalInput?: number | null;
        sums?: Partial<BankLoanSum> | BankLoanSums | null;
    } | null | undefined,
    lastYoaColumnDisplay?: {total: number} | null,
): number {
    if (!loan) return 0;
    const fallback = lastYoaColumnDisplay != null
        ? Number(lastYoaColumnDisplay.total) || 0
        : (Number(loan.currentTotalInput) || 0);
    return resolveBankLoanLastYoaTotal(loan.sums, fallback);
}

/** Drawer user-recalc payload includes schedule override keys (even when value is null). */
export function isBankLoanScheduleUserRecalc(
    sums: Partial<BankLoanSums> | BankLoanSum | null | undefined,
): boolean {
    if (!sums || typeof sums !== 'object') return false;
    return Object.prototype.hasOwnProperty.call(sums, 'prevFinalBalance')
        || Object.prototype.hasOwnProperty.call(sums, 'currFinalBalance')
        || Object.prototype.hasOwnProperty.call(sums, 'editedInstalments');
}

/**
 * After Drawer schedule init / user recalc: refresh base (split left) only;
 * keep every *Change (split right) — keyboard-owned, never cleared by schedule.
 */
export function mergeBankLoanSumsFromScheduleRecalc(
    existing: Partial<BankLoanSum> | null | undefined,
    computed: BankLoanSums,
): BankLoanSum {
    return mergeBankLoanSumsPreserveChanges(existing, computed);
}

/** One schedule API call; returns null when request incomplete or API fails. */
export async function fetchBankLoanScheduleRows(
    loan: Pick<BankLoanRow, 'loanAmt' | 'ratePct' | 'terms' | 'firstRepay'> | null | undefined,
    cache?: Map<string, LoanScheduleItemVO[] | null>,
): Promise<LoanScheduleItemVO[] | null> {
    const req = buildBankLoanScheduleRequest(loan);
    if (!req) return null;
    const key = bankLoanScheduleCacheKey(req);
    if (cache?.has(key)) return cache.get(key) ?? null;
    try {
        const resp = await calcBankLoanSchedule(req);
        const rows = Array.isArray(resp?.schedule) ? resp.schedule : [];
        cache?.set(key, rows);
        return rows;
    } catch (e) {
        console.error('[bankLoanSumCalc] calcBankLoanSchedule failed', e);
        cache?.set(key, null);
        return null;
    }
}
